/**
 * Permission taxonomy (stable string keys), shared by API and web.
 *
 * Resolution is ADDITIVE/UNION only (no DENY in v1). A user's effective
 * permissions are the union of the grants on every role assignment whose scope
 * is an ancestor-or-self of the target object. See {@link hasPermission} for
 * wildcard matching.
 */

export const PERMISSION_KEYS = [
  '*',
  // Org / settings / governance
  'org.read',
  'org.update',
  'org.transfer_ownership',
  'org.delete',
  'settings.read',
  'settings.manage',
  'billing.read',
  'billing.manage',
  'roles.read',
  'roles.manage',
  'members.read',
  'members.manage',
  'audit.read',
  // Sites
  'sites.read',
  'sites.create',
  'sites.update',
  'sites.delete',
  // Teams
  'teams.read',
  'teams.create',
  'teams.update',
  'teams.reparent',
  'teams.delete',
  'teams.members.manage',
  // Groups
  'groups.read',
  'groups.create',
  'groups.update',
  'groups.reparent',
  'groups.delete',
  'groups.members.manage',
  // People
  'people.read',
  'people.create',
  'people.update',
  'people.delete',
  'people.merge',
  'people.contact',
  'people.notes.read',
  'people.notes.write',
  'people.export',
  // Lists
  'lists.read',
  'lists.manage',
  'lists.materialize',
  // Events
  'events.read',
  'events.create',
  'events.update',
  'events.reparent',
  'events.delete',
  'events.signups.read',
  'events.signups.create',
  'events.signups.manage',
  // Check-in
  'checkins.self',
  'checkins.operate',
  'checkins.read',
  'checkins.manage',
  'checkins.pickup_override',
  'checkins.minors.read',
  // Finance
  'finance.read',
  'finance.manage',
  'finance.payments.read',
  'finance.payments.refund',
  'finance.reports.read',
  // Calendar
  'calendar.read',
  'calendar.manage',
  // Resources
  'resources.read',
  'resources.book',
  'resources.manage',
  // Content
  'content.read',
  'content.create',
  'content.update',
  'content.publish',
  'content.delete',
  // Media (object storage: uploads, attachments, photos)
  'media.read',
  'media.upload',
  'media.delete',
  // Forms
  'forms.read',
  'forms.create',
  'forms.update',
  'forms.delete',
  'forms.submit',
  'forms.responses.read',
  // Workflows
  'workflows.read',
  'workflows.manage',
  // Service planning
  'services.read',
  'services.create',
  'services.update',
  'services.delete',
  // Songs
  'songs.read',
  'songs.manage',
  // AI assist
  'ai.use',
  // Notifications
  'notifications.read',
  // Reporting & dashboards
  'reports.read',
  'reports.manage',
  // Support (tickets handled by the external FC EMS support system)
  'support.submit',
  'support.view',
] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

/**
 * Keys whose grant or use must be written to the audit log and which are
 * deliberately excluded from broad `*.*` operational defaults of lower roles.
 */
export const SENSITIVE_PERMISSION_KEYS: ReadonlySet<string> = new Set([
  '*',
  'org.delete',
  'org.transfer_ownership',
  'billing.read',
  'billing.manage',
  'roles.manage',
  'members.manage',
  'audit.read',
  'people.export',
  'people.merge',
  'people.notes.read',
  'people.notes.write',
  'finance.read',
  'finance.manage',
  'finance.payments.read',
  'finance.payments.refund',
  'finance.reports.read',
  'checkins.pickup_override',
  'checkins.minors.read',
  'workflows.manage',
  'lists.materialize',
])

/**
 * Does a set of granted keys satisfy a requested permission?
 *
 * - `*` grants everything.
 * - `events.*` grants `events.update`, `events.signups.read`, etc.
 * - exact key matches.
 */
export function hasPermission(grants: Iterable<string>, requested: string): boolean {
  const set = grants instanceof Set ? grants : new Set(grants)
  if (set.has('*')) return true
  if (set.has(requested)) return true
  const segments = requested.split('.')
  // Check every dotted prefix for a wildcard grant: a.b.c -> a.*, a.b.*
  for (let i = 1; i < segments.length; i++) {
    if (set.has(segments.slice(0, i).join('.') + '.*')) return true
  }
  return false
}

export interface PermissionCatalogEntry {
  key: string
  resource: string
  action: string
  isSensitive: boolean
}

/** The seedable global permission catalog derived from {@link PERMISSION_KEYS}. */
export function permissionCatalog(): PermissionCatalogEntry[] {
  return PERMISSION_KEYS.map((key) => {
    const dot = key.indexOf('.')
    const resource = key === '*' ? '*' : key.slice(0, dot === -1 ? undefined : dot)
    const action = key === '*' ? '*' : dot === -1 ? key : key.slice(dot + 1)
    return { key, resource, action, isSensitive: SENSITIVE_PERMISSION_KEYS.has(key) }
  })
}
